import React, { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { useWebSocket } from "contexts/WebSocketContext";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
  useMediaQuery,
  Chip,
  Tab,
  Tabs,
} from "@mui/material";
import MarkEmailReadRoundedIcon from "@mui/icons-material/MarkEmailReadRounded";
import ClearAllRoundedIcon from "@mui/icons-material/ClearAllRounded";
import { useAuth } from "contexts/AuthContext";
import { useStateContext } from "contexts/ContextProvider";
import { usePageTitle } from "hooks/usePageTitle";
import { useNavigate } from "react-router-dom";
import api from "services/api";
import { NAVBAR_HEIGHT } from "constants/layout";
import { OVERLAY_NAMES } from "constants/Overlays";
import dayjs from "dayjs";
// Tabs (2026-10-01, per Amine): group notifications into All /
// Interpretations / Translations / Reminders / Tags — the grouping lives in
// helpers/notificationGroups.js so the navbar bell popover shares it (2026-10-05).
import {
  NOTIFICATION_TABS,
  getNotificationGroupKey,
} from "helpers/notificationGroups";

const isVisibleNow = (notification) => {
  if (!notification?.VisibleAt) return true;
  const d = new Date(notification.VisibleAt);
  if (Number.isNaN(d.getTime())) return true;
  return d.getTime() <= Date.now();
};

const getNotificationTimestamp = (notification) => {
  const candidate = notification?.VisibleAt || notification?.createdAt;
  const parsed = new Date(candidate);
  return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
};

const parseReadFlag = (value) => {
  if (value === true || value === false) return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized === "1" || normalized === "true";
  }
  return false;
};

const normalizeNotification = (notification) => ({
  ...notification,
  IsRead: parseReadFlag(notification?.IsRead),
});

const sortNotificationsLatestFirst = (items) =>
  [...items].sort((a, b) => {
    const readBucketDifference = Number(parseReadFlag(a?.IsRead)) - Number(parseReadFlag(b?.IsRead));
    if (readBucketDifference !== 0) {
      return readBucketDifference;
    }

    return getNotificationTimestamp(b) - getNotificationTimestamp(a);
  });

const INITIAL_NOTIFICATIONS_LIMIT = 20;
const NEXT_NOTIFICATIONS_LIMIT = 10;

const mergeNotificationsById = (existingNotifications, incomingNotifications) => {
  const mergedById = new Map();

  existingNotifications.forEach((notification) => {
    mergedById.set(Number(notification.id), notification);
  });

  incomingNotifications.forEach((notification) => {
    mergedById.set(Number(notification.id), notification);
  });

  return Array.from(mergedById.values());
};

// Reminder Watch notifications carry the target date in their metadata.
// Falls back to tomorrow when the payload has no usable date.
const getReminderWatchTargetDate = (notification) => {
  const metadata = notification?.Metadata || {};
  const rawDate =
    metadata?.targetDate || metadata?.date || metadata?.reminderDate;
  if (rawDate) {
    const parsed = dayjs(rawDate);
    if (parsed.isValid()) return parsed;
  }
  return dayjs().add(1, "day");
};

const Notifications = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentUser } = useAuth();
  const { setOverlayData, setOverlayName, showSnackbar, openDialog, closeDialog } = useStateContext();
  const navigate = useNavigate();
  usePageTitle("Notifications");

  const { socket } = useWebSocket();

  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [markingAsRead, setMarkingAsRead] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const loadMoreRef = useRef(null);

  // Fetch first page (20)
  const fetchInitialNotifications = useCallback(async () => {
    if (!currentUser?.id) return;

    setLoading(true);
    try {
      const response = await api.get(`/notifications?limit=${INITIAL_NOTIFICATIONS_LIMIT}&offset=0`);
      const nextNotifications = Array.isArray(response.data)
        ? response.data.map(normalizeNotification)
        : [];
      setNotifications(sortNotificationsLatestFirst(nextNotifications));
      setHasMore(nextNotifications.length === INITIAL_NOTIFICATIONS_LIMIT);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      setNotifications([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  // Fetch next page (+10)
  const fetchMoreNotifications = useCallback(async () => {
    if (!currentUser?.id || loading || loadingMore || !hasMore) return;

    const offset = notifications.length;
    setLoadingMore(true);
    try {
      const response = await api.get(`/notifications?limit=${NEXT_NOTIFICATIONS_LIMIT}&offset=${offset}`);
      const nextNotifications = Array.isArray(response.data)
        ? response.data.map(normalizeNotification)
        : [];

      setNotifications((previousNotifications) => {
        const mergedNotifications = mergeNotificationsById(previousNotifications, nextNotifications);
        return sortNotificationsLatestFirst(mergedNotifications);
      });

      setHasMore(nextNotifications.length === NEXT_NOTIFICATIONS_LIMIT);
    } catch (error) {
      console.error("Failed to fetch more notifications:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [currentUser?.id, hasMore, loading, loadingMore, notifications.length]);

  // Format relative time (e.g., "2 hours ago")
  const formatRelativeTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);
    if (diffMinutes < 1) return "Just now";
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    const diffWeeks = Math.floor(diffDays / 7);
    if (diffWeeks < 4) return `${diffWeeks}w ago`;
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  };

  // Format notification type (keys are normalized to UPPERCASE so both the
  // legacy lowercase variants and the current server types resolve — the
  // chips for server-created notifications used to show the raw type).
  const getNotificationTypeLabel = (type) => {
    const normalized = String(type || "").toUpperCase();
    const typeMap = {
      INTERPRETATION_START_IN_MINUTES: "Interpretation Starting",
      INTERPRETATION_START_REMINDER: "Interpretation Starting",
      INTERPRETATION_END_HOURS_AGO: "Interpretation Ended",
      INTERPRETATION_END_FOLLOWUP: "Interpretation Ended",
      SERVICE_DEADLINE_IN_DAYS: "Upcoming Deadline",
      SERVICE_DEADLINE_REMINDER: "Upcoming Deadline",
      SERVICE_DEADLINE_TODAY: "Due Today",
      SERVICE_DEADLINE_DAYS_AGO: "Past Deadline",
      SERVICE_DEADLINE_PASSED: "Past Deadline",
      SERVICE_ASSIGNED: "Service Assigned",
      REMINDER_WATCH: "Reminder Watch",
      MENTION: "Mention",
      TASK_ASSIGNED: "Task Assigned",
      TASK_REASSIGNED: "Task Reassigned",
      TASK_DUE_SOON: "Task Due Soon",
      TASK_OVERDUE: "Task Overdue",
      SERVICE_COMMENT_CREATED: "New Comment",
      SERVICE_COMMENT_UPDATED: "Comment Updated",
    };
    return typeMap[normalized] || type;
  };

  // Get color for notification type (same UPPERCASE normalization).
  const getTypeColor = (type) => {
    switch (String(type || "").toUpperCase()) {
      case "INTERPRETATION_START_IN_MINUTES":
      case "INTERPRETATION_START_REMINDER":
        return "info";
      case "INTERPRETATION_END_HOURS_AGO":
      case "INTERPRETATION_END_FOLLOWUP":
        return "success";
      case "SERVICE_DEADLINE_IN_DAYS":
      case "SERVICE_DEADLINE_REMINDER":
      case "SERVICE_DEADLINE_TODAY":
      case "TASK_DUE_SOON":
      case "REMINDER_WATCH":
        return "warning";
      case "SERVICE_DEADLINE_DAYS_AGO":
      case "SERVICE_DEADLINE_PASSED":
      case "TASK_OVERDUE":
        return "error";
      case "SERVICE_ASSIGNED":
      case "TASK_ASSIGNED":
      case "TASK_REASSIGNED":
      case "SERVICE_COMMENT_CREATED":
      case "SERVICE_COMMENT_UPDATED":
      case "MENTION":
        return "info";
      default:
        return "default";
    }
  };

  // Mark single notification as read
  const handleMarkAsRead = useCallback(async (notificationId) => {
    const targetId = Number(notificationId);

    let wasAlreadyRead = false;
    setNotifications((prev) =>
      sortNotificationsLatestFirst(prev.map((notif) => {
        if (Number(notif.id) !== targetId) return notif;
        if (parseReadFlag(notif.IsRead)) {
          wasAlreadyRead = true;
          return notif;
        }

        return {
          ...notif,
          IsRead: true,
          ReadAt: notif.ReadAt || new Date().toISOString(),
        };
      }))
    );

    if (wasAlreadyRead) return;

    try {
      await api.patch(`/notifications/${targetId}/read`);
    } catch (error) {
      console.error("Failed to mark notification as read:", error);
      fetchInitialNotifications();
    }
  }, [fetchInitialNotifications]);

  // Mark all notifications as read
  const handleMarkAllAsRead = useCallback(async () => {
    setMarkingAsRead(true);
    try {
      await api.patch("/notifications/read-all");
      setNotifications((prev) =>
        sortNotificationsLatestFirst(prev.map((notif) => ({
          ...notif,
          IsRead: true,
          ReadAt: notif.ReadAt || new Date().toISOString(),
        })))
      );
      showSnackbar("All notifications marked as read.", theme, "success");
    } catch (error) {
      console.error("Failed to mark all notifications as read:", error);
      showSnackbar(
        "Couldn't mark all notifications as read — check your connection and try again.",
        theme,
        "error",
      );
    } finally {
      setMarkingAsRead(false);
      closeDialog();
    }
  }, [closeDialog, showSnackbar, theme]);

  const getNotificationRoute = useCallback((notification) => {
    const metadata = notification?.Metadata || {};

    if (typeof metadata?.routePath === "string" && metadata.routePath.startsWith("/")) {
      return metadata.routePath;
    }

    if (notification?.SourceType === "Service" && notification?.SourceID) {
      if (metadata?.serviceCategory === "Interpretation") {
        return `/interpretations/${notification.SourceID}`;
      }

      if (metadata?.serviceCategory === "Translation") {
        return `/translations/${notification.SourceID}`;
      }

      if (metadata?.serviceCategory === "Other") {
        return `/otherServices/${notification.SourceID}`;
      }

      return `/services/${notification.SourceID}`;
    }

    return null;
  }, []);

  const handleNotificationCardClick = useCallback(
    async (notification) => {
      // Reminder Watch notifications open the Interpretations Reminders
      // overlay for the target date carried in the payload. The overlay
      // opens immediately; mark-as-read is fire-and-forget so it never
      // blocks the click.
      if (notification?.Type === "reminder_watch") {
        setOverlayData({
          initialDate: getReminderWatchTargetDate(notification),
        });
        setOverlayName(OVERLAY_NAMES.INTERPRETATION_REMINDERS);
        navigate("/interpretations");

        if (!parseReadFlag(notification?.IsRead)) {
          void handleMarkAsRead(notification.id);
        }
        return;
      }

      const route = getNotificationRoute(notification);
      if (!route) return;

      if (!parseReadFlag(notification?.IsRead)) {
        await handleMarkAsRead(notification.id);
      }

      navigate(route);
    },
    [getNotificationRoute, handleMarkAsRead, navigate, setOverlayData, setOverlayName]
  );

  // Count unread
  const unreadCount = useMemo(
    () => notifications.filter((notification) => !parseReadFlag(notification.IsRead)).length,
    [notifications]
  );

  // Confirm "Mark All as Read" via the global dialog (portal convention). The
  // shared dialog disables its primary button and shows a spinner while the
  // async handler runs, so a double-click can't fire the PATCH twice.
  const handleMarkAllRequest = useCallback(() => {
    openDialog({
      theme,
      title: "Mark All as Read?",
      message: `This will mark all ${unreadCount} unread notification${unreadCount !== 1 ? "s" : ""} as read.`,
      primaryAction: "Mark as Read",
      primaryOnClick: () => handleMarkAllAsRead(),
      secondaryAction: "Cancel",
      secondaryOnClick: () => closeDialog(),
    });
  }, [closeDialog, handleMarkAllAsRead, openDialog, theme, unreadCount]);

  // Tab views (2026-10-01, per Amine): the list renders the active tab.
  const filteredNotifications = useMemo(
    () =>
      activeTab === "all"
        ? notifications
        : notifications.filter(
            (notification) =>
              getNotificationGroupKey(notification) === activeTab,
          ),
    [activeTab, notifications],
  );

  // Unread count per tab (shown in the tab labels).
  const tabUnreadCounts = useMemo(() => {
    const counts = { all: 0 };
    notifications.forEach((notification) => {
      if (parseReadFlag(notification.IsRead)) return;
      counts.all += 1;
      const key = getNotificationGroupKey(notification);
      if (key) counts[key] = (counts[key] || 0) + 1;
    });
    return counts;
  }, [notifications]);

  // When a specific tab is opened, page through the remaining notifications
  // (bounded) so the tab's contents and counts reflect the full list — the
  // page otherwise only holds the first 20 rows. Past the bound the normal
  // "Load more" observer keeps working.
  const fetchAllRemainingNotifications = useCallback(async () => {
    let offset = notifications.length;
    let more = hasMore;
    let guard = 0;
    const accumulated = [];

    while (more && guard < 25) {
      guard += 1;
      const response = await api.get(
        `/notifications?limit=${NEXT_NOTIFICATIONS_LIMIT}&offset=${offset}`,
      );
      const page = Array.isArray(response.data)
        ? response.data.map(normalizeNotification)
        : [];
      accumulated.push(...page);
      offset += page.length;
      more = page.length === NEXT_NOTIFICATIONS_LIMIT;
      if (page.length === 0) break;
    }

    if (accumulated.length > 0) {
      setNotifications((previousNotifications) =>
        sortNotificationsLatestFirst(
          mergeNotificationsById(previousNotifications, accumulated),
        ),
      );
    }
    setHasMore(more);
  }, [hasMore, notifications.length]);

  useEffect(() => {
    if (activeTab === "all" || !hasMore) return;
    fetchAllRemainingNotifications().catch((error) =>
      console.error("Failed to load all notifications:", error),
    );
    // Intentionally keyed to the tab only — the callback reads the latest
    // length/hasMore when it runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Initial load
  useEffect(() => {
    fetchInitialNotifications();
  }, [fetchInitialNotifications]);

  // Real-time: prepend newly created notifications from socket
  useEffect(() => {
    if (!socket) return;

    const handleNotificationCreated = ({ notification }) => {
      if (!notification) return;
      const incoming = normalizeNotification(notification);
      if (!isVisibleNow(incoming)) return;

      setNotifications((prev) => {
        if (prev.some((n) => Number(n.id) === Number(incoming.id))) return prev;
        return sortNotificationsLatestFirst([incoming, ...prev]);
      });
    };

    const handleNotificationUpdated = ({ notification }) => {
      if (!notification) return;
      const incoming = normalizeNotification(notification);
      const incomingId = Number(incoming.id);
      if (Number.isNaN(incomingId)) return;

      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => (Number(item.id) === incomingId ? { ...item, ...incoming } : item))
        )
      );
    };

    const handleNotificationReadAll = ({ userId, readAt }) => {
      if (Number(userId) !== Number(currentUser?.id)) return;

      setNotifications((prev) =>
        sortNotificationsLatestFirst(
          prev.map((item) => ({
            ...item,
            IsRead: true,
            ReadAt: item.ReadAt || readAt || new Date().toISOString(),
          }))
        )
      );
    };

    socket.on("notification:created", handleNotificationCreated);
    socket.on("notification:updated", handleNotificationUpdated);
    socket.on("notification:read-all", handleNotificationReadAll);
    return () => {
      socket.off("notification:created", handleNotificationCreated);
      socket.off("notification:updated", handleNotificationUpdated);
      socket.off("notification:read-all", handleNotificationReadAll);
    };
  }, [socket, currentUser?.id]);

  useEffect(() => {
    if (loading || !hasMore) return;
    const sentinel = loadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry?.isIntersecting && !loadingMore) {
          fetchMoreNotifications();
        }
      },
      {
        root: null,
        rootMargin: "300px 0px",
        threshold: 0,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [fetchMoreNotifications, hasMore, loading, loadingMore]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const totalNotifications = notifications.length;

  return (
    <Box
      sx={{
        p: { xs: "1rem", sm: "1.5rem", md: "2rem" },
        maxWidth: "900px",
        mx: "auto",
        minHeight: `calc(100vh - ${NAVBAR_HEIGHT}px)`,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Stack
          direction={isMobile ? "column" : "row"}
          spacing={2}
          sx={{
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
          }}
        >
          <Box>
            <Typography variant="body2" color="textSecondary">
              {totalNotifications === 0
                ? "No notifications"
                : unreadCount > 0
                ? `${unreadCount} unread of ${totalNotifications} total`
                : `${totalNotifications} notification${totalNotifications !== 1 ? "s" : ""}`}
            </Typography>
          </Box>

          {totalNotifications > 0 && (
            <Button
              variant="outlined"
              startIcon={<ClearAllRoundedIcon />}
              onClick={handleMarkAllRequest}
              disabled={unreadCount === 0 || markingAsRead}
              sx={{
                alignSelf: isMobile ? "stretch" : "auto",
              }}
            >
              Mark All as Read
            </Button>
          )}
        </Stack>
      </Box>

      {/* Tabs (2026-10-01, per Amine): All | Interpretations | Translations |
          Reminders | Tags — labels show each tab's unread count. */}
      {totalNotifications > 0 && (
        <Box
          sx={{
            mb: 2,
            borderBottom: 1,
            borderColor: theme.palette.primary.border2,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(event, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 42,
              "& .MuiTab-root": {
                textTransform: "none",
                fontWeight: 600,
                minHeight: 42,
                fontSize: "0.85rem",
              },
            }}
          >
            {NOTIFICATION_TABS.map((tab) => (
              <Tab
                key={tab.key}
                value={tab.key}
                label={`${tab.label}${
                  tabUnreadCounts[tab.key]
                    ? ` (${tabUnreadCounts[tab.key]})`
                    : ""
                }`}
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* Empty State */}
      {totalNotifications === 0 && (
        <Card
          sx={{
            p: 4,
            textAlign: "center",
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.primary.border2}`,
          }}
        >
          <MarkEmailReadRoundedIcon
            sx={{
              fontSize: 64,
              color: theme.palette.primary.main,
              mb: 2,
              opacity: 0.5,
            }}
          />
          <Typography variant="h6" color="textSecondary">
            All caught up!
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            You don't have any notifications at the moment.
          </Typography>
        </Card>
      )}

      {totalNotifications > 0 && (
        <Box>
          {filteredNotifications.length === 0 ? (
            <Card
              sx={{
                p: 3,
                textAlign: "center",
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${theme.palette.primary.border2}`,
              }}
            >
              <Typography variant="body2" color="textSecondary">
                No notifications in this tab.
              </Typography>
            </Card>
          ) : (
            <Stack spacing={1}>
              {filteredNotifications.map((notification) => (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={() => handleMarkAsRead(notification.id)}
                  onOpenProfile={() => handleNotificationCardClick(notification)}
                  formatRelativeTime={formatRelativeTime}
                  getNotificationTypeLabel={getNotificationTypeLabel}
                  getTypeColor={getTypeColor}
                  theme={theme}
                  isUnread={!parseReadFlag(notification.IsRead)}
                />
              ))}
            </Stack>
          )}

          {loadingMore && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 2,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}

          {hasMore && <Box ref={loadMoreRef} sx={{ height: 1 }} />}
        </Box>
      )}
    </Box>
  );
};

// Individual Notification Card Component
const NotificationCard = ({
  notification,
  onMarkAsRead,
  onOpenProfile,
  formatRelativeTime,
  getNotificationTypeLabel,
  getTypeColor,
  theme,
  isUnread,
}) => {
  const [hovering, setHovering] = React.useState(false);

  return (
    <Card
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      onClick={onOpenProfile}
      sx={{
        p: 2,
        backgroundColor: isUnread
          ? theme.palette.mode === "dark"
            ? "rgba(33, 150, 243, 0.08)"
            : "rgba(33, 150, 243, 0.04)"
          : theme.palette.background.paper,
        border: `1px solid ${
          isUnread ? theme.palette.primary.main : theme.palette.primary.border2
        }`,
        borderLeftWidth: isUnread ? "4px" : "1px",
        borderLeftColor: isUnread ? theme.palette.primary.main : "transparent",
        transition: "all 0.2s ease",
        cursor: onOpenProfile ? "pointer" : "default",
        "&:hover": {
          boxShadow: theme.shadows[2],
          backgroundColor: isUnread
            ? theme.palette.mode === "dark"
              ? "rgba(33, 150, 243, 0.12)"
              : "rgba(33, 150, 243, 0.06)"
            : theme.palette.action.hover,
        },
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        {/* Main Content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1, alignItems: "center" }}>
            <Chip
              label={getNotificationTypeLabel(notification.Type)}
              size="small"
              color={getTypeColor(notification.Type)}
              variant="outlined"
              sx={{ height: 24 }}
            />
            {isUnread && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  backgroundColor: theme.palette.primary.main,
                }}
              />
            )}
          </Stack>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: isUnread ? 600 : 500,
              mb: 0.5,
              wordBreak: "break-word",
            }}
          >
            {notification.Title}
          </Typography>

          <Typography
            variant="body2"
            color="textSecondary"
            sx={{
              mb: 1,
              wordBreak: "break-word",
              lineHeight: 1.5,
            }}
          >
            {notification.Message}
          </Typography>

          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              display: "block",
              fontSize: "0.8rem",
            }}
          >
            {formatRelativeTime(notification.VisibleAt || notification.createdAt)}
            {notification.ReadAt && (
              <>
                {" • "}
                <span style={{ fontStyle: "italic" }}>
                  Read {formatRelativeTime(notification.ReadAt)}
                </span>
              </>
            )}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            opacity: hovering ? 1 : 0.6,
            transition: "opacity 0.2s",
          }}
        >
          {isUnread && (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                onMarkAsRead();
              }}
              title="Mark as read"
              aria-label="Mark as read"
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <MarkEmailReadRoundedIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>

      {/* Metadata (Optional: Show Source Info if Available) */}
      {notification.Metadata && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography
            variant="caption"
            color="textSecondary"
            sx={{
              display: "block",
              fontSize: "0.75rem",
              opacity: 0.7,
            }}
          >
            {notification.SourceType && `Type: ${notification.SourceType}`}
            {notification.SourceID && ` • ID: ${notification.SourceID}`}
          </Typography>
        </>
      )}
    </Card>
  );
};

export default Notifications;
